import React, { useRef } from 'react';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { dunningSelector, selectUserId } from '@common-reducers/UserSelectors';
import { useRouter } from '@common-reducers/hooks/use-router';
import { assignDunningArm } from './dunning-arm';
import { useRecordDunningEventMutation } from '@common-reducers/backend-api/backend-api';

const DunningBanner: React.FC = () => {
    const { isDunning, dunningDays } = useAppSelector(dunningSelector);
    const userId = useAppSelector(selectUserId);
    const { navigate } = useRouter();

    const arm = assignDunningArm(userId);
    const [recordDunningEvent] = useRecordDunningEventMutation();
    const shownRef = useRef(false);

    // Fire dunning_banner_shown once per mount when isDunning is true.
    // Guard with a ref so StrictMode double-invoke and re-renders don't fire twice.
    React.useEffect(() => {
        if (!isDunning) return;
        if (shownRef.current) return;
        shownRef.current = true;
        // Fire-and-forget: never block render on event failure
        try {
            recordDunningEvent({
                name: 'dunning_banner_shown',
                props: { arm, dunningDays },
            }).catch(() => { /* intentionally ignored */ });
        } catch {
            // never break the banner
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDunning]);

    if (!isDunning) return null;

    const handleCtaClick = () => {
        // Fire-and-forget: never block on event failure. `arm` is tagged on the events
        // for a future behavioral A/B; today BOTH arms navigate to the working billing
        // portal (/admin-billing → Chargebee portal). The frictionless-modal variant is
        // deferred until a mounted card-update surface exists.
        try {
            recordDunningEvent({ name: 'dunning_cta_clicked', props: { arm } })
                .catch(() => { /* intentionally ignored */ });
            recordDunningEvent({ name: 'dunning_portal_opened', props: { arm } })
                .catch(() => { /* intentionally ignored */ });
        } catch {
            // never break the banner
        }
        navigate('/admin-billing');
    };

    return (
        <div style={{
            width: '100%',
            backgroundColor: '#dc2626',
            color: '#fff',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            fontSize: '14px',
            fontWeight: 500,
        }}>
            <span>
                Your payment has failed. Please update your payment method to avoid service interruption.
                {dunningDays > 0 && (
                    <strong> ({dunningDays} {dunningDays === 1 ? 'day' : 'days'} overdue)</strong>
                )}
            </span>
            <button
                onClick={handleCtaClick}
                style={{
                    backgroundColor: '#fff',
                    color: '#dc2626',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 16px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                }}
            >
                Update Payment
            </button>
        </div>
    );
};

export default DunningBanner;
